import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Guid } from 'guid-typescript';
import {
  Observable,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  of,
  switchMap,
} from 'rxjs';
import { AssemblyAreaModel } from 'src/app/models/assembly-area.model';
import { ColorsModel } from 'src/app/models/colors.model';
import { ItemCategoryModel } from 'src/app/models/item-category.model';
import { MeasurementUnitsModel } from 'src/app/models/measurement-units.model';
import { ProductModel } from 'src/app/models/products';
import {
  SapArticleModel,
  SapArticleType,
} from 'src/app/models/sap-article.model';
import { SapSubitem } from 'src/app/models/sap-subitem.model';
import { FileServerService } from 'src/app/services/file-server.service';
import { PoolColorsService } from 'src/app/services/pool-colors.service';
import { ProductService } from 'src/app/services/product.service';
import { SapArticleService } from 'src/app/services/sap-article.service';
import { ToastService } from 'src/app/services/toast.service';
import { ManufacturingItemEnum } from 'src/app/shared/enumerators/manufacturingItemEnum.enum';
import { CommonFunctionsConfiguratorService } from 'src/app/shared/services/swal-fire-configurator.service';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { FilterByProductNamePipe } from '../../shared/pipes/filter-by-product-name.pipe';

import { MatButton, MatIconButton } from '@angular/material/button';
import { EmptyStateComponent } from 'src/app/shared/ui/empty-state/empty-state.component';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';

@Component({
    selector: 'app-sap-articles',
    templateUrl: './sap-articles.component.html',
    styleUrls: ['./sap-articles.component.scss'],
    imports: [MatIcon, FormsModule, ReactiveFormsModule, MatFormField, MatInput, MatSelect, MatOption, MatSlideToggle, MatAutocompleteTrigger, MatAutocomplete, MatCheckbox, AsyncPipe, FilterByProductNamePipe, MatButton, MatIconButton, PageHeaderComponent, EmptyStateComponent]
})
export class SapArticlesComponent {
  private readonly productSvc = inject(ProductService);
  private readonly sapArtSvc = inject(SapArticleService);
  fileServerSrv = inject(FileServerService);
  toastr = inject(ToastService);
  private readonly router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  colorSvc = inject(PoolColorsService);
  private readonly swalFire = inject(CommonFunctionsConfiguratorService);

  searchText!: string;
  articlesList: SapArticleModel[] = [];
  products!: Observable<ProductModel[]>;
  itemCategories!: Observable<ItemCategoryModel[]>;
  assemblyAreas!: Observable<AssemblyAreaModel[]>;
  childItemCategories!: Observable<ItemCategoryModel[]>;
  childAssemblyAreas!: Observable<AssemblyAreaModel[]>;
  colorList: ColorsModel[] = [];
  showColors: boolean = false;
  types = ManufacturingItemEnum;
  productName: string = '';
  subItem: SapSubitem[] = [];
  sapTypes: SapArticleType[] = [];
  measureUnits: MeasurementUnitsModel[] = [];
  hasSubitems: boolean = false;
  private queryParamSubscription?: Subscription;

  subItemForm = new FormGroup({
    selectedChildAssemblyArea: new FormControl(),
    selectedChildItemCategory: new FormControl(),
    sonName: new FormControl(),
    sonAlias: new FormControl(),
    isCumulativeSon: new FormControl(false),
    isVisibleSon: new FormControl(false),
    isVisibleInDeliveryNoteSon: new FormControl(false),
  });

  articleForm = new FormGroup({
    id: new UntypedFormControl(Guid.EMPTY),
    name: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    internalCode: new FormControl('', Validators.required),
    alias: new FormControl('', [Validators.required, Validators.maxLength(30)]),
    isCumulative: new FormControl(true, Validators.required),
    isVisible: new FormControl(true, Validators.required),
    isVisibleInDeliveryNote: new FormControl(true, Validators.required),
    productId: new UntypedFormControl(null),
    productColors: new UntypedFormControl(null),
    type: new UntypedFormControl(null),
    items: new UntypedFormControl([]),
    measurementUnitId: new UntypedFormControl(null, Validators.required),
    assemblyAreaId: new UntypedFormControl(null, Validators.required),
    itemCategoryId: new UntypedFormControl(null, Validators.required),
  });

  constructor() {
    this.products = of([]);
  }

  getForm() {
    return new FormGroup({
      id: new UntypedFormControl(Guid.EMPTY),
      name: new FormControl('', Validators.required),
      internalCode: new FormControl('', Validators.required),
      alias: new UntypedFormControl('', Validators.required),
      productCategories: new UntypedFormControl([], Validators.required),
      isCumulative: new FormControl(true, Validators.required),
      isVisible: new FormControl(true, Validators.required),
      productId: new UntypedFormControl(null),
      productColors: new UntypedFormControl(null),
      type: new UntypedFormControl(null),
      items: new UntypedFormControl([]),
      measurementUnitId: new UntypedFormControl(null, Validators.required),
      assemblyAreaId: new UntypedFormControl(null, Validators.required),
      itemCategoryId: new UntypedFormControl(null, Validators.required),
    });
  }

  ngOnInit() {
    this.measureUnits.push({
      id: Guid.EMPTY,
      name: 'Ninguna',
      symbol: 'n',
      isActive: true,
    });

    this.articleForm.controls.productId.valueChanges
      .pipe(
        debounceTime(750),
        distinctUntilChanged(),
        switchMap((value: string) => {
          if (value == null) {
            value = '';
          }
          value = value.length < 3 ? "''" : value;
          return this.productSvc.search(value);
        })
      )
      .subscribe((data) => {
        this.products = of(data);
      });
    this.getProducts();
    this.getArticles();
    this.getItemCategories();
    this.getAssemblyArea();
    this.getSapArticlesTypes();
    this.getMeasurements();
    this._subscribeToReloadEvents();
  }

  getSapArticlesTypes() {
    this.sapArtSvc.getTypes().subscribe({
      next: (data) => {
        this.sapTypes = data;
      },
    });
  }

  getMeasurements() {
    this.sapArtSvc.getMeasurementUnits().subscribe({
      next: (data) => {
        this.measureUnits = [...this.measureUnits, ...data];
      },
    });
  }

  _subscribeToReloadEvents() {
    this.queryParamSubscription = this.activatedRoute.queryParams.subscribe(
      (params) => {
        let queryParams = { ...params };

        if (queryParams['manufacturingItemId']) {
          this.getArticleDetails(queryParams['manufacturingItemId']);
        }

        // Navigate to same url without the removed query parameter (this shouldn't refresh component)
        delete queryParams['manufacturingItemId'];
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: queryParams,
          replaceUrl: true, // same page: no extra history entry for Back/Volver to land on
        });
      }
    );
  }

  onSubmit() {
    if (!this.hasSelectedArticle()) {
      return;
    }
    if (
      this.articleForm.valid &&
      (this.subItemForm.valid || this.subItem.length > 0)
    ) {
      let artModel = this.articleForm.value as SapArticleModel;
      if (artModel.measurementUnitId == Guid.EMPTY) {
        artModel.measurementUnitId = null;
      }
      if (artModel.assemblyAreaId == Guid.EMPTY) {
        artModel.assemblyAreaId = null;
      }
      if (artModel.itemCategoryId == Guid.EMPTY) {
        artModel.itemCategoryId = null;
      }
      this.articleForm.controls['items'].patchValue(this.subItem);
      artModel.type = this.articleForm.controls.type.value;
      artModel.productColorId = this.articleForm.controls.productColors.value;
      this.sapArtSvc.patch(artModel).subscribe({
        next: (result) => {
          this.toastr.success(
            'Artículo actualizado',
            'El artículo se actualizó correctamente.'
          );
          this.swalFire
            .SwalFireQuestion(
              'Actualizar InternalCode',
              '¿Desea actualizar los InternalCode de las OA',
              'Actualizar',
              'Cancelar'
            )
            .then((result) => {
              if (result.isConfirmed) {
                this.updateInternalCode(artModel.internalCode);
              }
            });
          this.cleanForm();
          this.getArticles();
          this.hasSubitems = false;
        },
        error: (err) => {
          this.toastr.error('Error', 'No se pudo actualizar el artículo.');
        },
      });
    } else {
      this.articleForm.markAllAsTouched();
    }
  }

  updateInternalCode(internalCode: string) {
    this.sapArtSvc.updateInternalCodes(internalCode).subscribe({
      next: (data) => {
        this.toastr.success(
          'Internal code',
          'El internal code de ha actualizado corretamente en las órdenes de montaje.'
        );
      },
    });
  }

  async editArticle(art: SapArticleModel) {
    this.cleanForm();

    await this.getArticleDetails(art.id);
  }

  async getArticleDetails(artId: Guid) {
    return new Promise<void>(() => {
      this.cleanForm();
      this.sapArtSvc
        .getArticleById(artId)
        .subscribe((details: SapArticleModel) => {
          void this.applyArticleDetails(details);
        });
    });
  }

  private async applyArticleDetails(details: SapArticleModel): Promise<void> {
    let color: ColorsModel;
    if (details.productColorId) {
      color = await this.searchColor(details.productColorId);
      this.articleForm.controls.productColors.patchValue(color.id);
    }
    this.subItem = details.items;
    if (details.measurementUnitId == null) {
      details.measurementUnitId = Guid.EMPTY;
    }
    if (details.assemblyAreaId == null) {
      details.assemblyAreaId = Guid.EMPTY;
    }
    if (details.itemCategoryId == null) {
      details.itemCategoryId = Guid.EMPTY;
    }
    this.articleForm.patchValue(details);
    this.setShowColors();
  }

  async searchColor(id: Guid): Promise<ColorsModel> {
    let color: ColorsModel = await new Promise((resolve) => {
      this.colorSvc
        .getColorById(id)
        .subscribe({ next: (data: ColorsModel) => resolve(data) });
    });

    return color;
  }

  deleteArticle(art: SapArticleModel) {
    if (art.isActive) {
      this.swalFire
        .SwalFireQuestion(
          'Desactivar',
          '¿Desea desactivar este artículo?',
          'Desactivar',
          'Cancelar'
        )
        .then((result) => {
          if (result.isConfirmed) {
            this.sapArtSvc.deleteArticle(art.id, !art.isActive).subscribe({
              next: (result) => {
                this.toastr.success(
                  'Actualización',
                  'El artículo se desactivó correctamente.'
                );
                this.getArticles();
              },
              error: (err) => {
                this.toastr.error('Error', 'No se pudo desactivar el artículo.');
              },
            });
          }
        });
    } else {
      this.swalFire
        .SwalFireQuestion(
          'Activar',
          '¿Desea activar este artículo?',
          'Activar',
          'Cancelar'
        )
        .then((result) => {
          if (result.isConfirmed) {
            this.sapArtSvc.deleteArticle(art.id, !art.isActive).subscribe({
              next: (result) => {
                this.toastr.success(
                  'Actualización',
                  'El artículo se activó correctamente.'
                );
                this.getArticles();
              },
              error: (err) => {
                this.toastr.error(
                  'Error',
                  'No se pudo activar el artículo.'
                );
              },
            });
          }
        });
    }
  }

  getArticles() {
    this.sapArtSvc.getAllArticles().subscribe((data: SapArticleModel[]) => {
      this.articlesList = data;
    });
  }

  async getProducts() {
    await new Promise(() => {
      this.productSvc.get().subscribe((data) => {
        this.products = of(data);
      });
    });
  }

  async getItemCategories() {
    await new Promise(() => {
      this.sapArtSvc.getItemCategories().subscribe((data) => {
        this.itemCategories = of(data);
        this.childItemCategories = of(data);
      });
    });
  }

  async getAssemblyArea() {
    await new Promise(() => {
      this.sapArtSvc.getAssemblyArea().subscribe((data) => {
        this.assemblyAreas = of(data);
        this.childAssemblyAreas = of(data);
      });
    });
  }

  addSelectedItem() {
    if (this.subItemForm.valid) {
      this.subItem.push({
        alias: this.subItemForm.controls.sonAlias.value,
        name: this.subItemForm.controls.sonName.value,
        isVisible: this.subItemForm.controls.isVisibleSon.value,
        isVisibleInDeliveryNote:
          this.subItemForm.controls.isVisibleInDeliveryNoteSon.value,
        isCumulative: this.subItemForm.controls.isCumulativeSon.value,
        isActive: true,
        subItems: [],
        itemCategoryId: this.subItemForm.controls.selectedChildItemCategory.value.id,
        assemblyAreaId: this.subItemForm.controls.selectedChildAssemblyArea.value.id,
        itemCategoryName:
          this.subItemForm.controls.selectedChildItemCategory.value.displayName,
        assemblyAreaName:
          this.subItemForm.controls.selectedChildAssemblyArea.value.name,
        parentManufacturingItemId: this.articleForm.controls.id.value,
      });
      this.subItemForm.reset();
      this.subItemForm.controls.isCumulativeSon.patchValue(false);
      this.subItemForm.controls.isVisibleSon.patchValue(false);
      this.hasSubitems = false;
    } else {
      this.subItemForm.markAllAsTouched();
    }
  }

  removeSelectedItem(item: SapSubitem) {
    this.subItem = this.subItem.filter((x) => x.name != item.name);
    this.articleForm.controls.items.setValue(this.subItem);
  }

  async setColorList(id: Guid) {
    await new Promise(() => {
      this.products.subscribe((products: ProductModel[]) => {
        let prod = products.find((x: ProductModel) => x.id == id);
        if (prod) {
          this.colorList = prod.productColors;
        }
      });
    });
  }

  async setShowColors() {
    this.showColors = this.articleForm.controls.productId.value != null;
    this.selectedProductName(this.articleForm.controls.productId.value);
    if (this.showColors) {
      await this.setColorList(this.articleForm.controls.productId.value);
    }
  }

  selectedProductName(id: Guid) {
    if (id != null) {
      this.productSvc.getProduct(id).subscribe((x) => {
        this.productName = x.name;
      });
    }
  }

  // There is no create endpoint (only PATCH): the form can only update an article picked from the list.
  hasSelectedArticle(): boolean {
    const id = this.articleForm.controls.id.value;
    return !!id && id.toString() !== Guid.EMPTY;
  }

  /** The list row of the article open in the form (the form id is the selection). */
  isSelectedArticle(art: SapArticleModel): boolean {
    return this.hasSelectedArticle() && art.id?.toString() === this.articleForm.controls.id.value?.toString();
  }

  cleanForm() {
    this.articleForm.patchValue(this.getForm().value);
    this.articleForm.markAsUntouched();
    this.colorList = [];
    this.showColors = false;
    this.productName = '';
    this.getProducts();
    this.subItem = [];
    this.hasSubitems = false;
  }

  showSubItems(): boolean {
    this.hasSubitems = !this.hasSubitems;
    this.hasSubitems ? this.setValidators() : this.resetValidators();
    return this.hasSubitems;
  }

  resetValidators() {
    this.subItemForm.reset();
    this.subItemForm.clearValidators();
    this.subItemForm.updateValueAndValidity();
  }

  setValidators() {
    this.subItemForm.controls.sonName.setValidators(Validators.required);
    this.subItemForm.controls.sonAlias.setValidators(Validators.required);
    this.subItemForm.controls.selectedChildItemCategory.setValidators(
      Validators.required
    );
    this.subItemForm.controls.selectedChildAssemblyArea.setValidators(
      Validators.required
    );
  }

  onRowKeydown(event: KeyboardEvent, element: SapArticleModel) {
    if (event.key === 'Enter') {
      this.editArticle(element);
    }
  }

  keyDownSelect(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.setShowColors();
    }
  }
}
